import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { fetchPdfFromLogForViewing, generatePdfFromLog } from '../services/vehicleReportService';
import { getErrorMessage } from '../services/apiService';

interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  logId: string;
  vin: string; // For display purposes only
}

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isOpen,
  onClose,
  logId,
  vin,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen || !logId) return;

    let currentPdfUrl: string | null = null;

    const fetchPdf = async () => {
      setLoading(true);
      setError(null);
      setPdfUrl(null);

      try {
        const url = await fetchPdfFromLogForViewing(logId);
        currentPdfUrl = url;
        setPdfUrl(url);
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();

    // Cleanup blob URL on unmount or when modal closes
    return () => {
      if (currentPdfUrl) {
        window.URL.revokeObjectURL(currentPdfUrl);
      }
    };
  }, [isOpen, logId]);

  const handleDownload = async () => {
    if (!logId) return;

    setDownloading(true);
    try {
      await generatePdfFromLog({ logId });
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">NMVTIS Vehicle Report</h2>
            <p className="text-sm text-slate-400 font-mono">{vin}</p>
          </div>
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <>
                <button
                  onClick={handleOpenInNewTab}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-white/10"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in New Tab
                </button>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-white/10 disabled:opacity-50"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-[#AD0000] animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Generating PDF report...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 font-medium mb-2">Failed to generate report</p>
                <p className="text-slate-500 text-sm">{error}</p>
              </div>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={`NMVTIS Vehicle Report - ${vin}`}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-yellow-400 font-medium">No PDF available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfViewerModal;
